"use client";

import { useMemo } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { LayoutGridIcon, SearchIcon, ZapIcon } from "lucide-react";

import { usePermalinkPath } from "@/lib/permalinks-context";
import { useTranslator } from "@/lib/translation-context";

import type { TourDto } from "@tripcart/shared";

import { Button } from "@tripcart/traveler-ui/components/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@tripcart/traveler-ui/components/select";
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from "@tripcart/traveler-ui/components/tabs";

import {
  anyOnSale,
  applyFilters,
  deriveActivityOptions,
  deriveDurationOptions,
  parseFilters,
  type TourFilterState,
} from "./filters";
import { TourCard } from "../destinations/[slug]/_tour-card.client";
import gridStyles from "./static-tour-grid.module.css";
import styles from "./tour-browser.client.module.css";

/**
 * Which facet the host page already pins. Decides which param "Refine Search"
 * carries over to /tours, and whether the activity dropdown is worth showing.
 */
export type TourBrowserScope =
  | { kind: "destination"; slug: string; name: string }
  | { kind: "activity"; slug: string; name: string };

interface TourBrowserProps {
  tours: TourDto[];
  scope: TourBrowserScope;
  /** Give the first visible card `priority` — used by hosts whose hero is
   *  absent, making the first card the LCP element. */
  prioritizeFirstCard?: boolean;
}

/**
 * Client island for a taxonomy tour grid. Owns the filter row (tab pills +
 * cross-axis/duration selects + Refine Search) and renders the filtered grid.
 *
 * One component serves both the destination and the activity-type page, so the
 * row can't drift between them. `scope` says which facet the page already pins:
 * it decides what "Refine Search" carries over to /tours, and suppresses the
 * activity dropdown on an activity-type page, where it could only ever hold
 * that page's own single value.
 *
 * Filter state lives in the URL (?sale, ?type, ?duration) and is applied
 * client-side over the already-loaded tour set — no refetch, so the page's ISR
 * caching and sub-1s bar are untouched. Tabs and dropdowns apply instantly via
 * shallow URL replace.
 */
export function TourBrowser({
  tours,
  scope,
  prioritizeFirstCard = false,
}: TourBrowserProps): React.ReactElement {
  const t = useTranslator();
  const path = usePermalinkPath();
  const router = useRouter();
  const sp = useSearchParams();
  const state = parseFilters((k) => sp.get(k) ?? undefined);

  function commit(next: URLSearchParams): void {
    const qs = next.toString();
    router.replace(qs ? `?${qs}` : "?", { scroll: false });
  }

  function setSale(active: boolean): void {
    const p = new URLSearchParams(sp.toString());
    if (active) p.set("sale", "1");
    else p.delete("sale");
    commit(p);
  }

  function applyDropdowns(cross: string, duration: string): void {
    const p = new URLSearchParams(sp.toString());
    if (cross === "all") p.delete("type");
    else p.set("type", cross);
    if (duration === "all") p.delete("duration");
    else p.set("duration", duration);
    commit(p);
  }

  function clearAll(): void {
    const p = new URLSearchParams(sp.toString());
    for (const key of ["sale", "type", "duration"]) p.delete(key);
    commit(p);
  }

  // "Refine search" is a narrowing, not a fresh search: it hands the visitor to
  // /tours (price, months, sort — controls this row has no space for) with
  // everything they have already chosen still applied, INCLUDING the facet this
  // page is scoped to. Dropping that scope would make the one button labelled
  // "refine" the only one that widened the results.
  function handleRefine(): void {
    const p = new URLSearchParams();
    if (scope.kind === "destination") {
      p.set("dest", scope.slug);
      // `state.activity` is a NAME; /tours matches `?style=` on slug. Recover
      // the slug from a tour carrying that activity.
      const slug = activitySlugFor(tours, state.activity);
      if (slug) p.set("style", slug);
    } else {
      p.set("style", scope.slug);
    }
    if (state.duration) p.set("dur", state.duration);
    const qs = p.toString();
    router.push(`${path("tour")}${qs ? `?${qs}` : ""}`);
  }

  if (tours.length === 0) {
    return (
      <p className={gridStyles.empty}>
        {scope.kind === "destination"
          ? t("No tours published in {destinationName} yet.", {
              destinationName: scope.name,
            })
          : t("No {name} tours published yet.", { name: scope.name })}
      </p>
    );
  }

  const visibleTours = applyFilters(tours, state);

  return (
    <>
      <FilterRow
        tours={tours}
        scope={scope}
        state={state}
        onSetSale={setSale}
        onApply={applyDropdowns}
        onRefine={handleRefine}
      />
      {visibleTours.length === 0 ? (
        <NoMatches onReset={clearAll} />
      ) : (
        <div className={gridStyles.grid}>
          {visibleTours.map((tour, i) => (
            <TourCard
              key={tour.id}
              tour={tour}
              priority={prioritizeFirstCard && i === 0}
            />
          ))}
        </div>
      )}
    </>
  );
}

/**
 * `/tours` matches `?style=` on slug, but the axis holds a NAME. Recover the
 * slug from `styles[]` first — a tour's non-primary tags only carry their slug
 * there, so matching on `tour.activity`/`activitySlug` alone dropped the style
 * from the handoff and silently widened the results.
 */
function activitySlugFor(
  tours: TourDto[],
  activityName: string | null,
): string | undefined {
  if (!activityName) return undefined;
  for (const tour of tours) {
    const style = (tour.styles ?? []).find((s) => s.name === activityName);
    if (style?.slug) return style.slug;
    if (tour.activity === activityName && tour.activitySlug) {
      return tour.activitySlug;
    }
  }
  return undefined;
}

function FilterRow({
  tours,
  scope,
  state,
  onSetSale,
  onApply,
  onRefine,
}: {
  tours: TourDto[];
  scope: TourBrowserScope;
  state: TourFilterState;
  onSetSale: (active: boolean) => void;
  onApply: (cross: string, duration: string) => void;
  onRefine: () => void;
}): React.ReactElement {
  const t = useTranslator();

  // The activity axis is offered only where it can narrow anything: a
  // destination page holds tours of many activities, an activity-type page is
  // already one activity, so the same dropdown there would hold a single value.
  // Options come from the tours actually present, so a choice can never lead to
  // an empty grid.
  const activityOptions = useMemo(() => deriveActivityOptions(tours), [tours]);
  const cross =
    scope.kind === "destination"
      ? {
          label: t("Tour Type"),
          allLabel: t("All Types"),
          value: state.activity ?? "all",
          options: activityOptions.map((a) => ({ value: a, label: a })),
        }
      : null;

  // Only the bands this facet actually has. Offering all five meant four of
  // them dead-ended in "No tours match" wherever operators had not filled in a
  // duration.
  const durationOptions = useMemo(() => deriveDurationOptions(tours), [tours]);

  return (
    <div className={styles.filterRow}>
      <TabGroup
        sale={state.sale}
        showOnSales={anyOnSale(tours)}
        onSetSale={onSetSale}
      />

      <div className={styles.controls}>
        {/* A single-option dropdown is a control that cannot do anything —
            when every tour here shares one activity, the axis is already
            decided by the page itself. */}
        {cross && cross.options.length > 1 && (
          <FilterSelect
            label={cross.label}
            value={cross.value}
            onChange={(v) => onApply(v, state.duration ?? "all")}
            allLabel={cross.allLabel}
            options={cross.options}
          />
        )}
        {durationOptions.length > 0 && (
          <FilterSelect
            label={t("Duration")}
            value={state.duration ?? "all"}
            onChange={(v) => onApply(cross?.value ?? "all", v)}
            allLabel={t("Any Duration")}
            options={durationOptions.map((b) => ({
              value: b.id,
              label: b.label,
            }))}
          />
        )}
        <Button onClick={onRefine}>
          <SearchIcon strokeWidth={2} />
          {t("Refine Search")}
        </Button>
      </div>
    </div>
  );
}

function TabGroup({
  sale,
  showOnSales,
  onSetSale,
}: {
  sale: boolean;
  showOnSales: boolean;
  onSetSale: (active: boolean) => void;
}): React.ReactElement {
  const t = useTranslator();
  return (
    <Tabs
      value={sale ? "sale" : "all"}
      onValueChange={(value) => onSetSale(value === "sale")}
    >
      <TabsList variant="segmented" aria-label={t("Tour Filter")}>
        <TabsTrigger value="all">
          <LayoutGridIcon strokeWidth={2} />
          {t("All Tours")}
        </TabsTrigger>
        {showOnSales && (
          <TabsTrigger value="sale">
            <ZapIcon strokeWidth={2} />
            {t("On Sales")}
          </TabsTrigger>
        )}
      </TabsList>
    </Tabs>
  );
}

function FilterSelect({
  label,
  value,
  onChange,
  allLabel,
  options,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  allLabel: string;
  options: { value: string; label: string }[];
}): React.ReactElement {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger aria-label={label} className={styles.select}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">{allLabel}</SelectItem>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function NoMatches({ onReset }: { onReset: () => void }): React.ReactElement {
  const t = useTranslator();
  return (
    <div className={styles.noMatches}>
      <p className={styles.noMatchesText}>
        {t("No tours match these filters.")}
      </p>
      <Button variant="link" onClick={onReset} className={styles.clear}>
        {t("Clear Filters")}
      </Button>
    </div>
  );
}
